import React, { useEffect, useState } from 'react';
import { useGetSkeletonShelvesQuery, useLazyGetShelfItemsQuery } from './../redux/api-slice';
import ShelfItem from './Shelf';
import SkeletonItem from './SkeletonItem';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation } from 'swiper/modules';
import { SvgIcon } from '../../../ace/pages/calendar/components/svg-icon';


const GetShelves = ({ pageId }) => {
    const {
        data: shelves = [],
        isLoading: isLoadingShelves,
        isError: hasShelvesError
    } = useGetSkeletonShelvesQuery({ pageId });

    const [fetchShelfItems] = useLazyGetShelfItemsQuery();
    const [shelfItems, setShelfItems] = useState<Record<string, any[]>>({});
    const [loadingShelf, setLoadingShelf] = useState<Record<string, boolean>>({});

    const loadShelfItems = async (cleanShelfName: string) => {
        if (shelfItems[cleanShelfName]) return;

        setLoadingShelf(prev => ({ ...prev, [cleanShelfName]: true }));

        const result = await fetchShelfItems({ pageId, shelfName: cleanShelfName });
        const items = result.data || [];

        setShelfItems(prev => ({
            ...prev,
            [cleanShelfName]: Array.isArray(items) ? items : []
        }));

        setLoadingShelf(prev => ({ ...prev, [cleanShelfName]: false }));
    };

    useEffect(() => {
        if (!Array.isArray(shelves) || shelves.length === 0) return;

        shelves.forEach(shelf => {
            loadShelfItems(shelf.cleanShelfName);
        });
    }, [shelves, pageId]);

    return (
        <>
            {!hasShelvesError ?
                shelves.map((shelf, index) => {

                    const items = shelfItems[shelf.cleanShelfName] || [];
                    const isLoading = loadingShelf[shelf.cleanShelfName];
                    const shouldShowViewAll = items.length > 0 && shelf.shelfLimit > 0 && items.length === shelf.shelfLimit;

                    if (!isLoading && items.length === 0) {
                        return null;
                    }

                    return (

                        <div
                            id={shelf.shelfId}
                            key={shelf.shelfId || shelf.shelfName}
                            className={`digital-gallery-shelf-container swiper-container`}
                        >
                            <div className={`digital-gallery-shelf ${shelf.themeColor}  ${shelf.displayVertical ? "digital-gallery-shelf-vertical" : ""}`}>
                                <div className="swiper-container-wrapper">
                                    <div className="flex-between digital-gallery-shelf-header">
                                        <div className="stack-md">
                                            <h2 className="h5-style">{shelf.shelfName}</h2>
                                            {shelf.shelfDescription && (
                                                <p className="shelf-description rich-text">{shelf.shelfDescription}</p>
                                            )}
                                        </div>
                                        <div className="digital-gallery-shelf-actions">
                                            {shelf.shelfCTALink && shelf.shelfCTAText && (
                                                <div className="shelf-cta">
                                                    <a href={shelf.shelfCTALink} className="btn-primary shelf-cta-btn btn-full-width-sm">
                                                        {shelf.shelfCTAText}
                                                    </a>
                                                </div>
                                            )}
                                            <div className="digital-gallery-swiper-navigation theme-light">
                                                <button className={`media-controls-btn media-controls-prev-${index}`}>
                                                    <SvgIcon iconId="icon-arrow-left" />
                                                    <span className="visually-hidden">Previous</span>
                                                </button>
                                                <button className={`media-controls-btn media-controls-next-${index}`}>
                                                    <SvgIcon iconId="icon-arrow-right" />
                                                    <span className="visually-hidden">Next</span>
                                                </button>
                                            </div>

                                        </div>
                                    </div>

                                    <div className={`digital-gallery-swiper-wrapper`}>
                                        <Swiper
                                            modules={[Navigation]}
                                            navigation={{
                                                nextEl: `.media-controls-next-${index}`,
                                                prevEl: `.media-controls-prev-${index}`,
                                            }}
                                            spaceBetween={20}
                                            slidesPerView={"auto"}
                                            loop={false}
                                            watchOverflow={true}
                                            resistanceRatio={0}
                                            className="space-top-md"
                                        >
                                            {isLoading ? (
                                                Array.from({ length: shelf.itemCount }).map((_, i) => (
                                                    /*---SKELETON---*/
                                                    <SwiperSlide className="digital-gallery-shelf-item skeleton-item" key={`skeleton-${i}`}>
                                                        <div className="skeleton-body"></div>
                                                    </SwiperSlide>
                                                ))
                                            ) : (
                                                <>
                                                    {items.map((item, itemIndex) => (
                                                        <SwiperSlide key={item.id || `${shelf.shelfId}-${itemIndex}`}>
                                                            <ShelfItem item={item} />
                                                        </SwiperSlide>
                                                    ))}
                                                    {shouldShowViewAll && shelf.shelfCTALink && (
                                                        <SwiperSlide className=" digital-gallery-shelf-item-view-all">
                                                                <a href={shelf.shelfCTALink} className="unlink digital-gallery-shelf-item">

                                                                <div className="digital-gallery-shelf-item-overlay" tabIndex={-1}> </div>
                                                                <div className="digital-gallery-shelf-item-content">
                                                                    <h6 className="digital-gallery-shelf-item-title">
                                                                        View All </h6>
                                                                    <span className="visually-hidden">Next</span>
                                                                        <SvgIcon iconId="icon-external" cssClass={"svg-icon svg-icon-text"} /> 
                                                                </div>

                                                            </a>
                                                        </SwiperSlide>
                                                    )}
                                                </>
                                            )}
                                        </Swiper>
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })
                : <div className="error">Error loading shelves</div >}
        </>
    );
};

export default GetShelves;